/**
 * LogFilters - Filter controls for the Log Viewer
 * 
 * Provides filtering by:
 * - Log level (multi-select)
 * - Component (dropdown)
 * - Time range (presets + custom)
 * - Text search (debounced)
 * - Session ID
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Search, X, Clock, RefreshCw, ChevronDown } from 'lucide-react';

export interface LogFiltersState {
  level: string[];
  component: string;
  search: string;
  sessionId: string;
  startTime: string;
  endTime: string;
  timePreset: string;
}

interface LogFiltersProps {
  filters: LogFiltersState;
  onFiltersChange: (filters: LogFiltersState) => void;
  onApply: () => void;
  onClear: () => void;
  components: string[];
  isLoading?: boolean;
  levelCounts?: { debug: number; info: number; warn: number; error: number; performance: number };
}

const LOG_LEVELS = [
  { value: 'debug', label: 'Debug', color: 'bg-gray-500' },
  { value: 'info', label: 'Info', color: 'bg-blue-500' },
  { value: 'warn', label: 'Warn', color: 'bg-yellow-500' },
  { value: 'error', label: 'Error', color: 'bg-red-500' },
  { value: 'performance', label: 'Perf', color: 'bg-green-500' },
];

const TIME_PRESETS = [
  { value: '10s', label: 'Last 10 sec', ms: 10 * 1000 },
  { value: '60s', label: 'Last 60 sec', ms: 60 * 1000 },
  { value: '5m', label: 'Last 5 min', ms: 5 * 60 * 1000 },
  { value: '1h', label: 'Last 1 hour', ms: 60 * 60 * 1000 },
  { value: '24h', label: 'Last 24 hours', ms: 24 * 60 * 60 * 1000 },
  { value: '7d', label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000 },
  { value: 'custom', label: 'Custom', ms: 0 },
];

// ---------------------------------------------------------------------------
// Domain group definitions — maps component prefixes to human-readable groups
// ---------------------------------------------------------------------------

const COMPONENT_GROUPS: Array<{ prefix: string; label: string; color: string }> = [
  { prefix: 'comms.',              label: 'Communications',   color: 'bg-emerald-500' },
  { prefix: 'orchestration.',      label: 'Orchestration',    color: 'bg-blue-500' },
  { prefix: 'foreground.worker.',  label: 'Workers',          color: 'bg-purple-500' },
  { prefix: 'semantic_interpreter',label: 'Interpreter',      color: 'bg-amber-500' },
  { prefix: 'agent.',             label: 'Agents',           color: 'bg-indigo-500' },
  { prefix: 'email',              label: 'Email',            color: 'bg-cyan-500' },
  { prefix: 'intake',             label: 'Intake',           color: 'bg-rose-500' },
];

function getGroupForComponent(comp: string): string {
  for (const g of COMPONENT_GROUPS) {
    if (comp.startsWith(g.prefix)) return g.label;
  }
  return 'Other';
}

function groupComponents(components: string[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();
  for (const comp of components) {
    const group = getGroupForComponent(comp);
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push(comp);
  }
  // Sort: defined groups first in order, then 'Other' last
  const sorted = new Map<string, string[]>();
  for (const g of COMPONENT_GROUPS) {
    if (groups.has(g.label)) sorted.set(g.label, groups.get(g.label)!.sort());
  }
  if (groups.has('Other')) sorted.set('Other', groups.get('Other')!.sort());
  return sorted;
}

// ---------------------------------------------------------------------------
// ComponentFilter — searchable grouped combobox with quick-filter chips
// ---------------------------------------------------------------------------

function ComponentFilter({
  components,
  value,
  onChange,
}: {
  components: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const grouped = useMemo(() => groupComponents(components), [components]);

  const filteredGrouped = useMemo(() => {
    if (!search.trim()) return grouped;
    const q = search.toLowerCase();
    const result = new Map<string, string[]>();
    for (const [group, comps] of grouped) {
      // Match on group name or component name
      if (group.toLowerCase().includes(q)) {
        result.set(group, comps);
      } else {
        const matched = comps.filter(c => c.toLowerCase().includes(q));
        if (matched.length > 0) result.set(group, matched);
      }
    }
    return result;
  }, [grouped, search]);

  // Quick-filter chips — one per domain group that has components
  const quickFilters = useMemo(() => {
    const chips: Array<{ label: string; prefix: string; color: string; count: number }> = [];
    for (const g of COMPONENT_GROUPS) {
      const count = components.filter(c => c.startsWith(g.prefix)).length;
      if (count > 0) chips.push({ label: g.label, prefix: g.prefix, color: g.color, count });
    }
    return chips;
  }, [components]);

  const handleSelect = useCallback((comp: string) => {
    onChange(comp);
    setOpen(false);
    setSearch('');
  }, [onChange]);

  const handleQuickFilter = useCallback((prefix: string) => {
    // If already filtering on this prefix, clear it; otherwise set the prefix as a group filter
    onChange(value.startsWith(prefix) ? '' : prefix);
  }, [value, components, onChange]);

  const displayLabel = value || 'All Components';

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Quick-filter chips */}
      <div className="flex gap-1 flex-wrap">
        <button
          onClick={() => onChange('')}
          className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
            !value
              ? 'bg-gray-700 text-white dark:bg-gray-200 dark:text-gray-900'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          All
        </button>
        {quickFilters.map(qf => (
          <button
            key={qf.prefix}
            onClick={() => handleQuickFilter(qf.prefix)}
            className={`px-2 py-0.5 text-xs rounded-full transition-colors flex items-center gap-1 ${
              value.startsWith(qf.prefix)
                ? `${qf.color} text-white`
                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {qf.label}
            <span className="opacity-70 text-[10px]">{qf.count}</span>
          </button>
        ))}
      </div>

      {/* Searchable dropdown */}
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors min-w-[140px]"
        >
          <span className="truncate text-left flex-1">{displayLabel}</span>
          <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        </button>

        {open && (
          <div className="absolute top-full left-0 mt-1 w-72 max-h-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 flex flex-col">
            {/* Search input */}
            <div className="p-2 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter components..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoFocus
                  className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400"
                />
              </div>
            </div>

            {/* Grouped list */}
            <div className="overflow-y-auto flex-1">
              {/* All option */}
              <button
                onClick={() => handleSelect('')}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  !value ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                All Components
              </button>

              {[...filteredGrouped.entries()].map(([group, comps]) => (
                <div key={group}>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider bg-gray-50 dark:bg-gray-750 sticky top-0">
                    {group} ({comps.length})
                  </div>
                  {comps.map(comp => (
                    <button
                      key={comp}
                      onClick={() => handleSelect(comp)}
                      className={`w-full text-left px-4 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors truncate ${
                        value === comp
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {comp}
                    </button>
                  ))}
                </div>
              ))}

              {filteredGrouped.size === 0 && (
                <div className="px-3 py-4 text-sm text-gray-400 text-center">No matching components</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function LogFilters({
  filters,
  onFiltersChange,
  onApply,
  onClear,
  components,
  isLoading = false,
  levelCounts,
}: LogFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search);

  // Debounce search input and auto-apply for real-time search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        // Update filters with new search value
        const newFilters = { ...filters, search: searchInput };
        onFiltersChange(newFilters);
      }
    }, 400); // 400ms debounce for typing

    return () => clearTimeout(timer);
  }, [searchInput, filters, onFiltersChange]);

  // Sync local searchInput when filters.search changes externally
  useEffect(() => {
    if (filters.search !== searchInput) {
      setSearchInput(filters.search);
    }
  }, [filters.search]);

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.level.length > 0) count++;
    if (filters.component) count++;
    if (filters.search) count++;
    if (filters.sessionId) count++;
    if (filters.timePreset !== '1h') count++;
    return count;
  }, [filters]);

  const toggleLevel = (level: string) => {
    const newLevels = filters.level.includes(level)
      ? filters.level.filter(l => l !== level)
      : [...filters.level, level];
    onFiltersChange({ ...filters, level: newLevels });
  };

  const handleTimePresetChange = (preset: string) => {
    const presetConfig = TIME_PRESETS.find(p => p.value === preset);
    if (!presetConfig) return;

    if (preset === 'custom') {
      onFiltersChange({ ...filters, timePreset: preset });
    } else {
      const endTime = new Date().toISOString();
      const startTime = new Date(Date.now() - presetConfig.ms).toISOString();
      onFiltersChange({
        ...filters,
        timePreset: preset,
        startTime,
        endTime,
      });
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
      <div className="flex flex-wrap gap-4 items-center">
        {/* Level Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Level:</span>
          <div className="flex gap-1">
            {LOG_LEVELS.map(level => {
              const count = levelCounts?.[level.value as keyof typeof levelCounts] ?? 0;
              return (
                <button
                  key={level.value}
                  onClick={() => toggleLevel(level.value)}
                  className={`flex flex-col items-center px-2 py-1 text-xs rounded-md transition-colors min-w-[40px] ${
                    filters.level.includes(level.value)
                      ? `${level.color} text-white`
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <span>{level.label}</span>
                  <span className="text-[10px] font-mono opacity-80">{count.toLocaleString()}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Component Filter — Grouped searchable combobox with quick-filter chips */}
        <ComponentFilter
          components={components}
          value={filters.component}
          onChange={(comp) => onFiltersChange({ ...filters, component: comp })}
        />

        {/* Time Range */}
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500" />
          <select
            value={filters.timePreset}
            onChange={e => handleTimePresetChange(e.target.value)}
            className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {TIME_PRESETS.map(preset => (
              <option key={preset.value} value={preset.value}>{preset.label}</option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div className="flex-1 min-w-[200px] max-w-md relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search messages, components, source files..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-4 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400"
          />
        </div>

        {/* Session ID */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Session / Chat ID"
            value={filters.sessionId}
            onChange={e => onFiltersChange({ ...filters, sessionId: e.target.value })}
            className="w-32 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 font-mono"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 ml-auto">
          {activeFilterCount > 0 && (
            <span className="px-2 py-0.5 text-xs bg-lex-accent-soft text-lex-accent rounded-full">
              {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
            </span>
          )}
          
          <button
            onClick={onClear}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
            Clear
          </button>
          
          <button
            onClick={onApply}
            disabled={isLoading}
            className="flex items-center gap-1 px-4 py-1.5 text-sm bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Filters auto-apply — click to refresh now"
          >
            {isLoading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}
